import devOverrides from "./config.dev.json";
import prodOverrides from "./config.prod.json";
import stageOverrides from "./config.stage.json";
import defaults from "./defaults.json";
import type { EnvConfig } from "./schema";

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

type ConfigEnv = "dev" | "stage" | "prod";

function detectEnv(): ConfigEnv {
  // Mirror lib/app-env.ts's source-of-truth (GKE_ENV server-side,
  // NEXT_PUBLIC_RUNTIME_ENVIRONMENT inlined at build time and visible on the
  // client). Read process.env on every call so resetConfig() picks up env
  // mutations during tests.
  const env =
    process.env.GKE_ENV || process.env.NEXT_PUBLIC_RUNTIME_ENVIRONMENT || "";
  if (env === "production" || env === "prod" || env === "prod-v2")
    return "prod";
  if (env === "staging" || env === "stage" || env === "stage-v2")
    return "stage";
  return "dev";
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function deepMerge<T extends object>(base: T, override: DeepPartial<T>): T {
  const baseRecord = base as unknown as Record<string, unknown>;
  const overrideRecord = override as unknown as Record<string, unknown>;
  const result: Record<string, unknown> = { ...baseRecord };
  for (const key of Object.keys(overrideRecord)) {
    const val = overrideRecord[key];
    if (isPlainObject(val)) {
      const baseVal = baseRecord[key];
      const baseObj = isPlainObject(baseVal) ? baseVal : {};
      result[key] = deepMerge(
        baseObj,
        val as DeepPartial<Record<string, unknown>>,
      );
    } else if (val !== undefined) {
      result[key] = val;
    }
  }
  return result as unknown as T;
}

function applyEnvOverrides(
  config: EnvConfig,
  detectedEnv: ConfigEnv,
): EnvConfig {
  const appUrl = process.env.NEXT_PUBLIC_APP_URL;
  if (appUrl) config.app.url = appUrl.replace(/\/$/, "");

  // SECURITY: never let an env var downgrade auth cookies in production. A
  // misconfigured `USE_SECURE_COOKIES=false` on the prod deployment would
  // ship session cookies without the Secure flag — fail-fast at boot is
  // safer than serving requests in that state.
  if (process.env.USE_SECURE_COOKIES === "false") {
    if (detectedEnv === "prod") {
      throw new Error(
        "USE_SECURE_COOKIES=false is not allowed when running in production. " +
          "Refusing to start with insecure auth cookies in prod.",
      );
    }
    config.auth.useSecureCookies = false;
  }
  if (process.env.USE_SECURE_COOKIES === "true")
    config.auth.useSecureCookies = true;
  if (process.env.DEV_AUTH_BYPASS === "1")
    config.auth.enableDevAuthBypass = true;

  if (process.env.XAI_REASONING_MODEL)
    config.ai.grokReasoningModel = process.env.XAI_REASONING_MODEL;
  if (process.env.XAI_NON_REASONING_MODEL)
    config.ai.grokNonReasoningModel = process.env.XAI_NON_REASONING_MODEL;
  if (process.env.GROQ_TEXT_MODEL)
    config.ai.openaiFallbackModel = process.env.GROQ_TEXT_MODEL;

  if (process.env.R2_BUCKET_NAME)
    config.r2.bucketName = process.env.R2_BUCKET_NAME;

  return config;
}

const ENV_OVERRIDES: Record<ConfigEnv, DeepPartial<EnvConfig>> = {
  dev: devOverrides as DeepPartial<EnvConfig>,
  stage: stageOverrides as DeepPartial<EnvConfig>,
  prod: prodOverrides as DeepPartial<EnvConfig>,
};

let _config: EnvConfig | null = null;

export function getConfig(): EnvConfig {
  if (_config) return _config;

  const env = detectEnv();
  const overrides = ENV_OVERRIDES[env] ?? {};
  const merged = deepMerge(defaults as unknown as EnvConfig, overrides);
  _config = applyEnvOverrides(merged, env);

  return _config;
}

export function resetConfig(): void {
  _config = null;
}

export type { EnvConfig, RateLimitConfig, RateLimitEndpoint } from "./schema";
